/**
 * Maintenance Tasks View
 * Engineer-facing view showing assigned maintenance jobs
 * 
 * Features:
 * - DataGrid with all assigned jobs
 * - Filter by status (pending, overdue, completed)
 * - Quick actions: Start, Complete, Skip
 * - Job details modal
 * - Color-coded status badges
 */

import React, { useContext, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Tabs,
  Tab
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import {
  PlayArrow as StartIcon,
  CheckCircle as CompleteIcon,
  SkipNext as SkipIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  ViewList as TableIcon
} from '@material-ui/icons';
import { MaintenanceContext } from '../contexts/MaintenanceContext';
import { AuthContext } from '../contexts/AuthContext';
import MaintenanceDashboard from '../components/MaintenanceDashboard';
import MaintenanceCalendar from '../components/MaintenanceCalendar';
import JobStatusBadge from '../components/JobStatusBadge';
import NotificationBadges from '../components/NotificationBadges';
import moment from 'moment';

const MaintenanceTasks = () => {
  const { user } = useContext(AuthContext);
  const {
    jobs,
    isLoading,
    loadJobs,
    loadJobDetails,
    startJob,
    completeJob,
    skipJob,
    selectedJob,
    setSelectedJob
  } = useContext(MaintenanceContext);

  const [statusFilter, setStatusFilter] = useState('pending');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [currentTab, setCurrentTab] = useState(0); // 0 = Calendar, 1 = Table
  
  // Form fields for completion
  const [completionNotes, setCompletionNotes] = useState('');
  const [actualDowntime, setActualDowntime] = useState('');
  const [skipReason, setSkipReason] = useState('');

  // Load jobs on mount and when filter changes
  useEffect(() => {
    const filters = statusFilter && statusFilter !== 'all' 
      ? { status: statusFilter } 
      : {};
    loadJobs(filters);
  }, [statusFilter]);

  // Handle dashboard card clicks
  const handleDashboardFilter = (filter) => {
    if (filter.status) {
      setStatusFilter(filter.status);
    } else if (filter.dateRange === 'week') {
      setStatusFilter('all');
      // Could add date range filter here if needed
    } else if (filter.dateRange === 'month' && filter.status === 'completed') {
      setStatusFilter('completed');
    } else if (filter.pendingNotifications) {
      setStatusFilter('all');
      // Could filter to jobs needing notifications
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'secondary';
      case 'overdue':
        return 'error';
      case 'skipped':
        return 'default';
      default:
        return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  // Handle view details
  const handleViewDetails = async (jobId) => {
    setSelectedJobId(jobId);
    await loadJobDetails(jobId);
    setDetailsDialogOpen(true);
  };

  // Handle start job
  const handleStartJob = async (jobId) => {
    const success = await startJob(jobId);
    if (success) {
      loadJobs({ status: statusFilter === 'all' ? undefined : statusFilter });
    }
  };

  // Handle complete job - open dialog
  const handleCompleteJobDialog = (jobId) => {
    setSelectedJobId(jobId);
    setCompletionNotes('');
    setActualDowntime('');
    setCompleteDialogOpen(true);
  };

  // Handle complete job - submit
  const handleCompleteJobSubmit = async () => {
    const success = await completeJob(selectedJobId, {
      completion_notes: completionNotes,
      actual_downtime_minutes: actualDowntime ? parseInt(actualDowntime) : null
    });
    
    if (success) {
      setCompleteDialogOpen(false);
      loadJobs({ status: statusFilter === 'all' ? undefined : statusFilter });
    }
  };

  // Handle skip job - open dialog
  const handleSkipJobDialog = (jobId) => {
    setSelectedJobId(jobId);
    setSkipReason('');
    setSkipDialogOpen(true);
  };

  // Handle skip job - submit
  const handleSkipJobSubmit = async () => {
    if (!skipReason.trim()) {
      return;
    }
    
    const success = await skipJob(selectedJobId, skipReason);
    
    if (success) {
      setSkipDialogOpen(false);
      loadJobs({ status: statusFilter === 'all' ? undefined : statusFilter });
    }
  };

  // DataGrid columns
  const columns = [
    {
      field: 'scheduled_date',
      headerName: 'Date',
      width: 120,
      valueFormatter: (params) => moment(params.value).format('MMM DD, YYYY')
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180
    },
    {
      field: 'schedule_title',
      headerName: 'Task',
      width: 250,
      flex: 1
    },
    {
      field: 'asset_name',
      headerName: 'Asset',
      width: 150
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.75} style={{ padding: '4px 0' }}>
          <JobStatusBadge
            status={params.value}
            clientNotified={params.row.client_notified_at}
            engineerReminded={params.row.engineer_reminded_at}
            size="small"
          />
          <NotificationBadges
            clientNotified={params.row.client_notified_at}
            engineerReminded={params.row.engineer_reminded_at}
            notificationDaysBefore={params.row.notification_days_before}
            scheduledDate={params.row.scheduled_date}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row.id)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {params.row.status === 'pending' && (
            <Tooltip title="Start Task">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleStartJob(params.row.id)}
              >
                <StartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {(params.row.status === 'pending' || params.row.status === 'in_progress') && (
            <>
              <Tooltip title="Complete">
                <IconButton
                  size="small"
                  style={{ color: '#4caf50' }}
                  onClick={() => handleCompleteJobDialog(params.row.id)}
                >
                  <CompleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Skip">
                <IconButton
                  size="small"
                  onClick={() => handleSkipJobDialog(params.row.id)}
                >
                  <SkipIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          My Maintenance Tasks
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="skipped">Skipped</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => loadJobs({ status: statusFilter === 'all' ? undefined : statusFilter })}
              disabled={isLoading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Dashboard Metrics */}
      <MaintenanceDashboard 
        jobs={jobs} 
        onFilterClick={handleDashboardFilter}
        isLoading={isLoading}
      />

      {/* Tabs */}
      <Paper>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<CalendarIcon />} label="Calendar" />
          <Tab icon={<TableIcon />} label="Table" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box mt={2}>
        {currentTab === 0 && (
          <MaintenanceCalendar
            jobs={jobs}
            onCompleteJob={handleCompleteJobDialog}
            onSkipJob={handleSkipJobDialog}
          />
        )}

        {currentTab === 1 && (
          <Paper style={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={jobs}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={isLoading}
              disableSelectionOnClick
              density="comfortable"
            />
          </Paper>
        )}
      </Box>

      {/* Job Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedJob?.job?.schedule_title || 'Job Details'}
            </Typography>
            {selectedJob?.job && (
              <Chip
                label={getStatusLabel(selectedJob.job.status)}
                color={getStatusColor(selectedJob.job.status)}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedJob?.job ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Client:</strong> {selectedJob.job.client_name}
              </Typography>
              
              {selectedJob.job.asset_name && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Asset:</strong> {selectedJob.job.asset_name}
                </Typography>
              )}
              
              <Typography variant="subtitle1" gutterBottom>
                <strong>Scheduled Date:</strong>{' '}
                {moment(selectedJob.job.scheduled_date).format('MMMM DD, YYYY')}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedJob.job.schedule_description || 'No description provided'}
              </Typography>
              
              {selectedJob.job.completion_notes && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>Completion Notes:</strong>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedJob.job.completion_notes}
                  </Typography>
                </>
              )}
              
              {selectedJob.job.actual_downtime_minutes && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Actual Downtime:</strong> {selectedJob.job.actual_downtime_minutes} minutes
                </Typography>
              )}
              
              {selectedJob.job.completed_at && (
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Completed:</strong>{' '}
                  {moment(selectedJob.job.completed_at).format('MMMM DD, YYYY h:mm A')}
                  {selectedJob.job.completed_by_name && ` by ${selectedJob.job.completed_by_name}`}
                </Typography>
              )}
              
              {/* History */}
              {selectedJob.history && selectedJob.history.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>History:</strong>
                  </Typography>
                  {selectedJob.history.map((h) => (
                    <Box key={h.id} mb={1}>
                      <Typography variant="body2">
                        <strong>{h.user_name}</strong> {h.action} -{' '}
                        {moment(h.created_at).format('MMM DD, YYYY h:mm A')}
                      </Typography>
                      {h.notes && (
                        <Typography variant="body2" color="textSecondary">
                          {h.notes}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Complete Task</DialogTitle>
        
        <DialogContent>
          <TextField
            label="Completion Notes"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            margin="normal"
            placeholder="Describe what was done, any issues encountered, etc."
          />
          
          <TextField
            label="Actual Downtime (minutes)"
            type="number"
            fullWidth
            variant="outlined"
            value={actualDowntime}
            onChange={(e) => setActualDowntime(e.target.value)}
            margin="normal"
            placeholder="Optional: How many minutes of downtime occurred?"
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Close
          </Button>
          <Button
            onClick={handleCompleteJobSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading}
          >
            Complete Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip Job Dialog */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Skip Task</DialogTitle>
        
        <DialogContent>
          <TextField
            label="Reason for Skipping *"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={skipReason}
            onChange={(e) => setSkipReason(e.target.value)}
            margin="normal"
            placeholder="Why is this task being skipped?"
            required
            error={skipReason.trim() === ''}
            helperText={skipReason.trim() === '' ? 'Reason is required' : ''}
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setSkipDialogOpen(false)}>
            Close
          </Button>
          <Button
            onClick={handleSkipJobSubmit}
            color="secondary"
            variant="contained"
            disabled={isLoading || !skipReason.trim()}
          >
            Skip Task
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MaintenanceTasks;
